import React, { useMemo, useState } from 'react';
import { Box, Field, FieldTypes, OptionKey, RadioOption, Text } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { VALIDATORS } from '@/common';
import { CreateContactFormModel } from '@/modules/employer/types/Contact.types';

import ContactVerificationHint from '../components/ContactVerificationHint';
import EmailVerificationWarningMessage from '../components/EmailVerificationWarningMessage';
import ResendVerificationHint from '../components/ResendVerificationHint';
import { ContactResponseModel, JubFunctionEnum } from '../ContactSecurity.types';
import { RemovingAccountManagerAssignment } from '../RemovingAccountManagerAssignment/RemovingAccountManagerAssignment';
import { useOrganizationsByAccountManagerIdQuery } from '../RemovingAccountManagerAssignment/useOrganizationsByAccountManagerId.query';

export const hasAccountManagerJobFunction = (jobFunctionTypes?: JubFunctionEnum[]) =>
  jobFunctionTypes?.some((functionType) => functionType === JubFunctionEnum.ACCOUNT_MANAGER);
export const getAccountManagerFubFunction = (formValue?: boolean, actualData?: JubFunctionEnum[]) => [
  ...(typeof formValue === 'boolean'
    ? [...(formValue ? [JubFunctionEnum.ACCOUNT_MANAGER] : [])]
    : actualData || []),
];

interface Args {
  data: Partial<ContactResponseModel>;
  firstContact?: boolean;
  hasPrimary: boolean;
  onlyOnePrimaryContact: boolean;
  onOpenPermissionTable(): void;
  viewMode?: boolean;
  adminSsoOnly?: boolean;
  errors?: {
    [key: string]: string;
  };
}
interface ExtendedRadioOption extends RadioOption {
  id: OptionKey;
  key: OptionKey;
  title?: string;
}

const USER_NAME_REQUIRED = 'User Name is a required field';
const EMAIL_REQUIRED = 'Email is a required field';
const EMAIL_VALID = 'Email must be valid.';
const PHONE_REQUIRED = 'Phone number is required';

const useContactSecurityForm = (args: Args, permissionsList: ExtendedRadioOption[], values: CreateContactFormModel | null) => {
  const { errors = {} } = args;
  const temporaryPasswordField: Field[] = useMemo(
    () =>
      !args.viewMode
        ? [
            {
              name: 'temporaryPassword',
              type: FieldTypes.Info,
              label: 'Temporary password',
              value: 'After adding a user, a temporary password will be generated automatically.',
            },
          ]
        : [],
    [args.viewMode]
  );

  const userNameValidation = useMemo(() => {
    if (errors.username) {
      return VALIDATORS.STRING.test({
        test: (val) => val !== errors.username,
        message: 'Username format is invalid',
      }).required(USER_NAME_REQUIRED);
    }
    if (errors.usernameExists) {
      return VALIDATORS.STRING.test({
        test: (val) => val !== errors.usernameExists,
        message: 'Username format is already used',
      }).required(USER_NAME_REQUIRED);
    }
    return VALIDATORS.STRING.required(USER_NAME_REQUIRED);
  }, [errors]);

  const emailValidation = useMemo(() => {
    if (errors.email) {
      return VALIDATORS.STRING.test({
        test: (val) => val !== errors.email,
        message: 'The email is invalid',
      })
        .required(EMAIL_REQUIRED)
        .email(EMAIL_VALID);
    }
    if (errors.emailAlreadyExists) {
      return VALIDATORS.STRING.test({
        test: (val) => val !== errors.emailAlreadyExists,
        message: 'This is email is already used by another contact',
      })
        .required(EMAIL_REQUIRED)
        .email(EMAIL_VALID);
    }
    return VALIDATORS.REQUIRED_STRING.email(EMAIL_VALID);
  }, [errors]);

  const { names, isLoading } = useOrganizationsByAccountManagerIdQuery(toString(args?.data?.id));
  const [isUnassignedAsAccountManagerToOthers, setIsUnassignedAsAccountManagerToOthers] = useState(false);

  const isEmailChanged = useMemo(() => args?.data?.email && values?.email && args?.data?.email !== values?.email,
    [values?.email, args?.data?.email]);

  const showEmailVerificationWarningMessage = useMemo(() => {
    if (args.viewMode) return;
    return (!isEmailChanged && typeof args?.data?.is_email_verified === 'boolean' && !args?.data?.is_email_verified) || isEmailChanged;
  }, [isEmailChanged, args?.data?.is_email_verified, args.viewMode]);

  const fields = useMemo(
    (): Field[] => [
      {
        name: 'firstName',
        type: FieldTypes.Text,
        label: 'First name',
        showRequireIcon: true,
        placeholder: 'First name',
        validator: VALIDATORS.REQUIRED_STRING,
        defaultValue: args.data.first_name || '',
      },
      {
        name: 'lastName',
        type: FieldTypes.Text,
        label: 'Last name',
        showRequireIcon: true,
        placeholder: 'Last name',
        validator: VALIDATORS.REQUIRED_STRING,
        defaultValue: args.data.last_name || '',
      },
      {
        name: 'email',
        type: FieldTypes.Email,
        label: 'Email',
        showRequireIcon: true,
        placeholder: 'Email',
        validator: emailValidation,
        defaultValue: args?.data?.email || '',
        ...args?.data?.email && !args?.viewMode ? {
          rightIcon: <ContactVerificationHint isVerified={!!args?.data?.is_email_verified && !isEmailChanged} showWarning />,
        } : {},
        ...args?.data?.email && args?.viewMode && !args?.data?.is_email_verified ? {
          valueRenderWrapper: (emailNode) => (
            <ResendVerificationHint
              emailNode={emailNode}
              contactId={args?.data?.id}
              email={args?.data?.email}
            />),
        } : {},
      },
      ...showEmailVerificationWarningMessage ? [{
        name: 'emailVerificationWarningMessage',
        type: FieldTypes.Node,
        value: (
          <EmailVerificationWarningMessage
            firstName={args.data.first_name}
            lastName={args.data.last_name}
          />
        )
      }] as Field[] : [],
      ...(args?.adminSsoOnly
        ? []
        : ([
            {
              name: 'userName',
              type: FieldTypes.Text,
              label: !args.viewMode ? (
                <Box>
                  <Text margin={{ bottom: 'xsmall' }} weight="bold">
                    Username
                  </Text>
                  <Box>
                    {!args.data.external_identifier && (
                      <Text weight="normal" style={{ width: '200px' }} color="textSecondary">
                        Username field can be set up only once and cannot be updated later.
                      </Text>
                    )}
                  </Box>
                </Box>
              ) : (
                'Username'
              ),
              showRequireIcon: true,
              placeholder: 'Username',
              validator: userNameValidation,
              defaultValue: args.data.username || '',
              disabled: !!args.data.id && !!args.data.username,
              isLowerCaseMode: true,
            },
          ] as Field[])),
      {
        name: 'externalIdentifier',
        type: FieldTypes.Text,
        label: 'Unique ID',
        placeholder: 'Unique ID',
        validator: errors.uniqueId
          ? VALIDATORS.STRING.test({
              test: (val) => val !== errors.uniqueId,
              message: 'Unique ID already exists in the system',
            }).nullable()
          : VALIDATORS.STRING,

        // .required('Unique ID is a required field')
        // .test('len', 'Must be exactly numbers', (val) => Boolean(val && REGEXPS.ONLY_NUMBERS.test(val))),
        defaultValue: args.data.external_identifier || '',
        // disabled: !!args.data.id,
      },
      {
        name: 'phoneNumber',
        type: FieldTypes.PhoneNumber,
        label: 'Phone number',
        placeholder: '+1 (999) 999-9999',
        validator: errors.phone
          ? VALIDATORS.STRING.test({
              test: (val) => val !== errors.phone,
              message: 'Phone number is invalid',
            }).required(PHONE_REQUIRED)
          : VALIDATORS.STRING,

        defaultValue: args.data.phone || '',
      },
      {
        name: 'title',
        type: FieldTypes.Text,
        label: 'Title/role',
        placeholder: 'Title/role',
        defaultValue: args.data.title || '',
      },
      {
        name: 'jobFunction',
        type: FieldTypes.Checkbox,
        label: 'Job function',
        placeholder: 'Account Manager',
        ...(hasAccountManagerJobFunction(args?.data?.job_function_types)
          ? {
              defaultValue: true,
            }
          : {}),
        ...(names?.length && !isUnassignedAsAccountManagerToOthers
          ? {
              helpText: (
                <RemovingAccountManagerAssignment
                  contact={args?.data}
                  names={names}
                  onSuccess={() => setIsUnassignedAsAccountManagerToOthers(true)}
                />
              ),
            }
          : {
              helpText: 'Account Manager',
            }),
        disabled: Boolean(isLoading || names?.length) && !isUnassignedAsAccountManagerToOthers,
      },
      {
        name: 'department',
        type: FieldTypes.Text,
        label: 'Department',
        placeholder: 'Department',
        defaultValue: args.data.department || '',
      },
      ...temporaryPasswordField,
      {
        name: 'primary',
        type: FieldTypes.Checkbox,
        label: 'Set as primary contact',
        placeholder: 'Doing Business As',
        validator: VALIDATORS.BOOLEAN_REQUIRED,
        defaultValue: args.firstContact ? true : args.data.primary || false,
        disabled: args.firstContact || Boolean(args.onlyOnePrimaryContact && args.data.primary),
      },
      permissionsList.length > 7
        ? {
            name: 'roleId',
            type: FieldTypes.Dropdown,
            isPositionSensitive: true,
            singleMode: true,
            options: permissionsList.map((item) => ({
              key: item.key,
              value: item.title,
            })),
            label: (
              <Box>
                <Text margin={{ bottom: 'xsmall' }} weight="bold">
                  Permissions
                </Text>
              </Box>
            ),
            showRequireIcon: true,
            placeholder: 'Select Permission',
            validator: VALIDATORS.REQUIRED_STRING,
            defaultValue: args.data.roleId || '',
          }
        : {
            name: 'roleId',
            type: FieldTypes.Radio,
            options: permissionsList,
            label: (
              <Box>
                <Text margin={{ bottom: 'xsmall' }} weight="bold">
                  Permissions
                </Text>
              </Box>
            ),
            placeholder: 'Select Permission',
            validator: VALIDATORS.REQUIRED_STRING,
            defaultValue: args.data.roleId || '',
          },
    ],
    [
      args,
      errors,
      permissionsList,
      userNameValidation,
      temporaryPasswordField,
      names,
      isLoading,
      setIsUnassignedAsAccountManagerToOthers,
      isUnassignedAsAccountManagerToOthers,
      emailValidation,
      isEmailChanged,
      showEmailVerificationWarningMessage,
    ],
  );
  return fields;
};

export default useContactSecurityForm;
